import { dateToMs, fmtMsTick, fmtMsTickDay, fmtUsdCompact } from '@/lib/finance/format'

/** Shared recharts axis/grid props per the mark specs: recessive hairlines,
 * muted tabular tick text, clean compact dollar ticks. */

export const xAxisProps = {
  tickLine: false,
  axisLine: { stroke: 'var(--baseline)' },
  tick: { fill: 'var(--text-muted)', fontSize: 11 },
  tickMargin: 8,
} as const

/** Time-scaled x-axis for the balance/net-worth series. Points carry a numeric
 * `t` (epoch ms via dateToMs), so a month-end gap and a one-day gap render at
 * their true spacing — daily data no longer bunches up at the right. Spread
 * onto an <XAxis/> alongside a per-chart minTickGap. */
export const timeXAxisProps = {
  ...xAxisProps,
  dataKey: 't',
  type: 'number' as const,
  scale: 'time' as const,
  domain: ['dataMin', 'dataMax'] as ['dataMin', 'dataMax'],
  tickFormatter: fmtMsTick,
} as const

/** Tick granularity matched to the SELECTED time range (not the data that
 * happens to fill it): month-scale selections — MTD/1M/3M/6M and custom
 * windows up to ~6 months — get day labels ("Jul 16"), since month labels
 * would repeat identically across the axis; year-scale selections (1Y and up,
 * All) keep month labels ("Jul '26"). Pass the page range's resolved
 * [startMonth, endMonth] bounds; spread AFTER timeXAxisProps to override its
 * static formatter. */
export function timeTickFormatter(rangeStart: string, rangeEnd: string): (ms: number) => string {
  const span = dateToMs(rangeEnd) - dateToMs(rangeStart)
  return span <= 190 * 86_400_000 ? fmtMsTickDay : fmtMsTick
}

export const yAxisProps = {
  tickLine: false,
  axisLine: false as const,
  tick: { fill: 'var(--text-muted)', fontSize: 11 },
  tickFormatter: (v: number) => fmtUsdCompact(v),
  width: 52,
}

export const gridProps = {
  vertical: false,
  stroke: 'var(--gridline)',
  strokeWidth: 1,
} as const

/** Max bar thickness (spec: ≤ 24px) and rounded data-end. */
export const BAR_MAX = 24
export const BAR_RADIUS: [number, number, number, number] = [4, 4, 0, 0]

/** The one chart margin every finance chart uses. */
export const CHART_MARGIN = { top: 6, right: 6, bottom: 0, left: 0 } as const

/** Hover dot for line/area series — ringed in the card surface color. */
export const ACTIVE_DOT = { r: 4, stroke: 'var(--surface-1)', strokeWidth: 2 } as const

/** Cap on points per line/area chart. The daily-balance model produces
 * thousands of dates; recharts renders every one as SVG path work, which is
 * what makes a chart feel slow to draw. ~480 points is denser than any
 * screen can show anyway. Tables keep full fidelity — only charts sample. */
export const MAX_CHART_POINTS = 480

/** Downsample to chart density, always keeping the first and last row. With a
 * `value` accessor, each bucket keeps its min- and max-valued rows so a
 * short-lived spike or dip survives thinning (a uniform stride can step right
 * over it); without one, rows are kept at a uniform stride. */
export function downsample<T>(
  rows: T[],
  value?: (row: T) => number | null | undefined,
  max: number = MAX_CHART_POINTS,
  domain?: (row: T) => number,
): T[] {
  if (rows.length <= max) return rows
  // Buckets are even spans of TIME, not row count. Balance history is
  // mixed-density — daily since Dec 2023, monthly (month-end) 2020–2023,
  // scattered days before — so an index-even split makes a bucket ≈ a week
  // of recent data but ≈ 7 MONTHS of the sparse era, thinning genuinely
  // monthly history into a quarterly-looking line. Time-even buckets flip
  // that: each sparse-era point owns its bucket (all survive) and the dense
  // era absorbs the compression. Rows are the finance charts' date keys
  // ("YYYY-MM-DD" / "YYYY-MM") unless a caller passes its own domain; a
  // non-date shape without one falls back to the old index-even behavior.
  const dom = domain ?? (typeof rows[0] === 'string' ? (r: T) => dateToMs(r as unknown as string) : undefined)
  const t0 = dom ? dom(rows[0]) : 0
  const t1 = dom ? dom(rows[rows.length - 1]) : 0
  const timed = dom !== undefined && t1 > t0
  if (!value) {
    if (!timed) {
      const step = (rows.length - 1) / (max - 1)
      const out: T[] = new Array(max)
      for (let i = 0; i < max; i++) out[i] = rows[Math.round(i * step)]
      return out
    }
    // Nearest row to each of `max` evenly spaced instants (single pass).
    const out: T[] = []
    let i = 0
    for (let k = 0; k < max; k++) {
      const target = t0 + (k * (t1 - t0)) / (max - 1)
      while (i < rows.length - 1 && Math.abs(dom!(rows[i + 1]) - target) <= Math.abs(dom!(rows[i]) - target)) i++
      if (out.length === 0 || out[out.length - 1] !== rows[i]) out.push(rows[i])
    }
    return out
  }
  const buckets = Math.max(1, Math.floor(max / 2))
  const bucketOf = timed
    ? (i: number) => Math.min(buckets - 1, Math.floor(((dom!(rows[i]) - t0) / (t1 - t0)) * buckets))
    : (i: number) => Math.min(buckets - 1, Math.floor(i / (rows.length / buckets)))
  const keep = new Set<number>([0, rows.length - 1])
  // Walk rows grouped by bucket (empty time-buckets simply never appear),
  // keeping each bucket's min AND max so short-lived extremes survive.
  let lo = 0
  while (lo < rows.length) {
    const b = bucketOf(lo)
    let hi = lo + 1
    while (hi < rows.length && bucketOf(hi) === b) hi++
    let minI = -1
    let maxI = -1
    let minV = 0
    let maxV = 0
    for (let i = lo; i < hi; i++) {
      const v = value(rows[i])
      if (v == null) continue
      if (minI === -1 || v < minV) { minI = i; minV = v }
      if (maxI === -1 || v > maxV) { maxI = i; maxV = v }
    }
    if (minI === -1) keep.add(lo)
    else {
      keep.add(minI)
      keep.add(maxI)
    }
    lo = hi
  }
  return rows.filter((_, i) => keep.has(i))
}
